// Registrul seturilor de date importabile — SINGURA sursă de adevăr pentru:
//   - ce coloane așteaptă fiecare dashboard (antet template)
//   - cum se validează și se convertesc valorile dintr-un Excel/CSV
//   - ce câmpuri sunt obligatorii (le completează clientul din WinMentor/Optima)
//     vs. calculate automat (le derivăm noi — clientul nu face aritmetică).
//
// Coloanele sunt aliniate 1:1 cu tipurile din src/types.ts și cu JSON-urile demo.

import { computeMediaMobila, type DatasetKey } from "./store";

// ─── Tipuri descriptor ───────────────────────────────────────────────────────
type ColType = "luna" | "number" | "text" | "enum" | "date";

export interface ColumnDef {
  name: string; // antetul exact așteptat
  type: ColType;
  required: boolean; // clientul TREBUIE să-l completeze
  auto?: boolean; // calculat automat dacă lipsește
  enumValues?: string[];
  descriere: string; // ce reprezintă (afișat în panou)
}

export interface DatasetDef {
  key: DatasetKey;
  titlu: string;
  descriere: string;
  sursaSugerata: string; // de unde iese exportul (hint pentru client)
  fileName: string; // numele template-ului CSV
  columns: ColumnDef[];
  exampleRows: string[][]; // ordine identică cu `columns`
  postProcess?: (rows: RawRow[]) => RawRow[];
}

type RawRow = Record<string, unknown>;

// ─── Helpere de conversie (RO-friendly) ──────────────────────────────────────
const r1 = (n: number) => Math.round(n * 10) / 10;
const r2 = (n: number) => Math.round(n * 100) / 100;

function coerceNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  let s = String(v ?? "").trim();
  if (!s) return null;
  s = s.replace(/\s/g, "").replace(/€|eur|%/gi, "");
  const hasComma = s.includes(",");
  const hasDot = s.includes(".");
  if (hasComma && hasDot)
    s = s.replace(/\./g, "").replace(",", "."); // 1.234,56 -> 1234.56
  else if (hasComma) s = s.replace(",", "."); // 210,03 -> 210.03
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function serialToLuna(serial: number): string | null {
  // Serial Excel (zile de la 1899-12-30) — apare când Excel salvează AAAA-LL ca dată.
  if (serial < 1 || serial > 80000) return null;
  const ms = Date.UTC(1899, 11, 30) + serial * 86400000;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function coerceLuna(v: unknown): string | null {
  if (typeof v === "number") return serialToLuna(v);
  const s = String(v ?? "").trim();
  let m = s.match(/^(\d{4})[-/.](\d{1,2})$/); // 2025-07, 2025/7, 2025.07
  if (m) {
    const mo = Number(m[2]);
    if (mo < 1 || mo > 12) return null;
    return `${m[1]}-${String(mo).padStart(2, "0")}`;
  }
  m = s.match(/^(\d{1,2})[-/.](\d{4})$/); // 07-2025, 7/2025
  if (m) {
    const mo = Number(m[1]);
    if (mo < 1 || mo > 12) return null;
    return `${m[2]}-${String(mo).padStart(2, "0")}`;
  }
  return null;
}

function serialToDate(serial: number): string | null {
  if (serial < 1 || serial > 80000) return null;
  const ms = Date.UTC(1899, 11, 30) + serial * 86400000;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

/** Normalizează o dată la ISO YYYY-MM-DD. Acceptă ISO, DD/MM/AAAA, DD.MM.AAAA, serial Excel. */
function coerceDate(v: unknown): string | null {
  if (typeof v === "number") return serialToDate(v);
  const s = String(v ?? "").trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/); // 2025-07-08
  if (m) {
    const mo = Number(m[2]);
    const zi = Number(m[3]);
    if (mo < 1 || mo > 12 || zi < 1 || zi > 31) return null;
    return `${m[1]}-${String(mo).padStart(2, "0")}-${String(zi).padStart(2, "0")}`;
  }
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/); // 08/07/2025, 8.7.2025
  if (m) {
    const zi = Number(m[1]);
    const mo = Number(m[2]);
    if (mo < 1 || mo > 12 || zi < 1 || zi > 31) return null;
    return `${m[3]}-${String(mo).padStart(2, "0")}-${String(zi).padStart(2, "0")}`;
  }
  return null;
}

function daysBetween(a: string, b: string): number {
  const da = new Date(a + "T00:00:00Z").getTime();
  const db = new Date(b + "T00:00:00Z").getTime();
  if (Number.isNaN(da) || Number.isNaN(db)) return 0;
  return Math.round((db - da) / 86400000);
}

function norm(h: string): string {
  return String(h)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[ăâ]/g, "a")
    .replace(/î/g, "i")
    .replace(/ș|ş/g, "s")
    .replace(/ț|ţ/g, "t");
}

// ─── Rezultatul unei parsări ─────────────────────────────────────────────────
export interface ParseResult {
  ok: boolean;
  rows?: RawRow[];
  count?: number;
  errors: string[];
}

/** Citește primul sheet dintr-un fișier .xlsx/.xls/.csv într-un array de obiecte.
 *  SheetJS e încărcat lazy (doar la primul import) ca să nu îngreuneze bundle-ul. */
export async function readFileToRows(file: File): Promise<RawRow[]> {
  const XLSX = await import("xlsx");
  const buf = await file.arrayBuffer();
  // raw:true → NU ghici tipuri la CSV (altfel „2026-01" devine serial de dată).
  const wb = XLSX.read(buf, { type: "array", raw: true, cellDates: false });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return [];
  const ws = wb.Sheets[sheetName];
  return XLSX.utils.sheet_to_json<RawRow>(ws, { defval: "", raw: true, blankrows: false });
}

/** Validează + convertește rândurile brute conform descriptorului de dataset. */
export function validateAndCoerce(def: DatasetDef, raw: RawRow[]): ParseResult {
  const errors: string[] = [];
  if (!raw.length)
    return { ok: false, errors: ["Fișierul nu conține rânduri de date (doar antet sau gol)."] };

  const firstKeys = Object.keys(raw[0]);
  const headerMap = new Map<string, string>();
  for (const k of firstKeys) headerMap.set(norm(k), k);

  // 1) Coloane obligatorii prezente?
  const missing = def.columns
    .filter((c) => c.required && !c.auto)
    .filter((c) => !headerMap.has(norm(c.name)));
  if (missing.length) {
    errors.push(`Lipsesc coloane obligatorii: ${missing.map((m) => `«${m.name}»`).join(", ")}.`);
    errors.push(`Coloane găsite în fișier: ${firstKeys.join(", ") || "(niciuna)"}.`);
    errors.push("Descarcă template-ul de mai jos pentru antetul corect.");
    return { ok: false, errors };
  }

  // 2) Conversie celulă cu celulă.
  const out: RawRow[] = [];
  raw.forEach((row, idx) => {
    const rowNum = idx + 2; // +1 antet, +1 index 1-based
    const obj: RawRow = {};
    for (const c of def.columns) {
      const actual = headerMap.get(norm(c.name));
      const val = actual !== undefined ? row[actual] : undefined;
      const gol = val === undefined || val === null || String(val).trim() === "";

      if (c.auto) continue; // recalculat în postProcess — ignorăm ce e în fișier
      if (gol) {
        errors.push(`Rând ${rowNum}: coloana obligatorie «${c.name}» e goală.`);
        continue;
      }
      if (c.type === "number") {
        const n = coerceNumber(val);
        if (n === null) {
          errors.push(`Rând ${rowNum}, «${c.name}»: „${val}" nu e număr valid.`);
          continue;
        }
        obj[c.name] = n;
      } else if (c.type === "luna") {
        const lk = coerceLuna(val);
        if (!lk) {
          errors.push(
            `Rând ${rowNum}, «${c.name}»: „${val}" nu e lună validă (aștept AAAA-LL, ex. 2025-07).`,
          );
          continue;
        }
        obj[c.name] = lk;
      } else if (c.type === "date") {
        const d = coerceDate(val);
        if (!d) {
          errors.push(
            `Rând ${rowNum}, «${c.name}»: „${val}" nu e dată validă (aștept AAAA-LL-ZZ, ex. 2025-07-08).`,
          );
          continue;
        }
        obj[c.name] = d;
      } else if (c.type === "enum") {
        const s = String(val).trim();
        const match = c.enumValues!.find((e) => e.toLowerCase() === s.toLowerCase());
        if (!match) {
          errors.push(
            `Rând ${rowNum}, «${c.name}»: „${s}" invalid. Valori acceptate: ${c.enumValues!.join(", ")}.`,
          );
          continue;
        }
        obj[c.name] = match;
      } else {
        obj[c.name] = String(val).trim();
      }
    }
    out.push(obj);
  });

  if (errors.length) {
    const shown = errors.slice(0, 8);
    if (errors.length > 8) shown.push(`… și încă ${errors.length - 8} probleme de corectat.`);
    return { ok: false, errors: shown };
  }

  const processed = def.postProcess ? def.postProcess(out) : out;
  return { ok: true, rows: processed, count: processed.length, errors: [] };
}

// ─── Template CSV (antet + 1-2 rânduri exemplu) ──────────────────────────────
function csvCell(v: string): string {
  return /[";\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** Generează conținutul CSV al template-ului (delimitator „;", zecimale cu virgulă — RO Excel). */
export function buildTemplateCsv(def: DatasetDef): string {
  const headers = def.columns.map((c) => c.name);
  const lines = [headers, ...def.exampleRows].map((r) => r.map((c) => csvCell(String(c))).join(";"));
  return "﻿" + lines.join("\r\n"); // BOM pentru diacritice în Excel
}

// ─── Registrul ────────────────────────────────────────────────────────────────
export const DATASETS: DatasetDef[] = [
  {
    key: "cost_productie",
    titlu: "1. Cost producție /oră și /unitate",
    descriere: "Cost total lunar pe centrele productive, ore lucrate și unități (m² echiv.) produse.",
    sursaSugerata: "WinMentor — cost centre de cost / pontaj + Optima — producție lunară",
    fileName: "template_01_cost_productie.csv",
    columns: [
      { name: "luna_key", type: "luna", required: true, descriere: "Luna (AAAA-LL)" },
      { name: "cost_total_eur", type: "number", required: true, descriere: "Cost total lună (€)" },
      { name: "ore_lucrate", type: "number", required: true, descriere: "Ore lucrate în producție" },
      { name: "unitati_m2", type: "number", required: true, descriere: "Unități produse (m² echiv.)" },
      { name: "cost_pe_ora_eur", type: "number", required: false, auto: true, descriere: "= cost_total / ore (auto)" },
      { name: "cost_pe_unitate_eur", type: "number", required: false, auto: true, descriere: "= cost_total / unități (auto)" },
    ],
    exampleRows: [
      ["2025-07", "169998,86", "809,4", "352,2", "210,03", "482,69"],
      ["2025-08", "172724,33", "843,9", "423,0", "204,67", "408,34"],
    ],
    postProcess: (rows) =>
      rows.map((r) => {
        const cost = r.cost_total_eur as number;
        const ore = r.ore_lucrate as number;
        const uni = r.unitati_m2 as number;
        return {
          ...r,
          cost_pe_ora_eur: ore > 0 ? r2(cost / ore) : 0,
          cost_pe_unitate_eur: uni > 0 ? r2(cost / uni) : 0,
        };
      }),
  },
  {
    key: "media_mobila",
    titlu: "2. Media mobilă cost producție (1/6/12 luni)",
    descriere:
      "Se calculează AUTOMAT din setul Cost producție — nu e nevoie să-l încarci separat. Îl poți importa doar dacă vrei valori proprii.",
    sursaSugerata: "Derivat din setul Cost producție (nu necesită export separat)",
    fileName: "template_02_media_mobila.csv",
    columns: [
      { name: "luna_key", type: "luna", required: true, descriere: "Luna (AAAA-LL)" },
      { name: "cost_pe_ora_eur", type: "number", required: true, descriere: "Cost /oră lună (€)" },
      { name: "ma_1l", type: "number", required: false, auto: true, descriere: "Media 1 lună (auto)" },
      { name: "ma_6l", type: "number", required: false, auto: true, descriere: "Media 6 luni (auto)" },
      { name: "ma_12l", type: "number", required: false, auto: true, descriere: "Media 12 luni (auto)" },
      { name: "semnal_vs_ma6", type: "enum", required: false, auto: true, enumValues: ["SUB_MEDIE", "PESTE_MEDIE"], descriere: "Semnal vs MA6 (auto)" },
      { name: "deviatie_pct_ma6", type: "number", required: false, auto: true, descriere: "Deviație % vs MA6 (auto)" },
    ],
    exampleRows: [
      ["2025-07", "210,03", "210,03", "210,03", "210,03", "SUB_MEDIE", "0"],
      ["2025-08", "204,67", "204,67", "207,35", "207,35", "SUB_MEDIE", "-1,3"],
    ],
    postProcess: (rows) => {
      const cost = rows.map((r) => ({
        luna_key: r.luna_key as string,
        cost_total_eur: 0,
        ore_lucrate: 0,
        unitati_m2: 0,
        cost_pe_ora_eur: r.cost_pe_ora_eur as number,
        cost_pe_unitate_eur: 0,
      }));
      return computeMediaMobila(cost) as unknown as RawRow[];
    },
  },
  {
    key: "profitabilitate",
    titlu: "3. Profitabilitate per client / categorie produs",
    descriere: "Venit, cost și nr. comenzi pe fiecare client sau categorie de produs.",
    sursaSugerata: "WinMentor — facturi vânzare + costuri comandă (grupat pe client / articol)",
    fileName: "template_03_profitabilitate.csv",
    columns: [
      { name: "dimensiune", type: "enum", required: true, enumValues: ["client", "categorie_produs"], descriere: "client sau categorie_produs" },
      { name: "valoare", type: "text", required: true, descriere: "Numele clientului / categoriei" },
      { name: "nr_comenzi", type: "number", required: true, descriere: "Număr comenzi" },
      { name: "venit_eur", type: "number", required: true, descriere: "Venit total (€)" },
      { name: "cost_eur", type: "number", required: true, descriere: "Cost total (€)" },
      { name: "marja_eur", type: "number", required: false, auto: true, descriere: "= venit − cost (auto)" },
      { name: "marja_pct", type: "number", required: false, auto: true, descriere: "= marjă / venit (auto)" },
      { name: "prag_pret_min_pct", type: "number", required: false, auto: true, descriere: "= cost / venit (auto)" },
    ],
    exampleRows: [
      ["categorie_produs", "geam_tren", "29", "19918,66", "13417,10", "6501,56", "32,6", "67,4"],
      ["client", "SNCF Voyageurs", "18", "24500,00", "16800,00", "7700,00", "31,4", "68,6"],
    ],
    postProcess: (rows) =>
      rows.map((r) => {
        const venit = r.venit_eur as number;
        const cost = r.cost_eur as number;
        const marja = venit - cost;
        return {
          ...r,
          marja_eur: r2(marja),
          marja_pct: venit > 0 ? r1((marja / venit) * 100) : 0,
          prag_pret_min_pct: venit > 0 ? r1((cost / venit) * 100) : 0,
        };
      }),
  },
  {
    key: "incarcare_masini",
    titlu: "4a. Încărcare mașini vs. capacitate",
    descriere: "Cantitatea produsă lunar pe fiecare mașină vs. capacitatea teoretică.",
    sursaSugerata: "Optima — raport producție pe utilaj / centru de lucru",
    fileName: "template_04a_incarcare_masini.csv",
    columns: [
      { name: "nume_masina", type: "text", required: true, descriere: "Denumire mașină / utilaj" },
      { name: "luna_key", type: "luna", required: true, descriere: "Luna (AAAA-LL)" },
      { name: "zile_active", type: "number", required: true, descriere: "Zile active în lună" },
      { name: "cant_produsa", type: "number", required: true, descriere: "Cantitate produsă" },
      { name: "unitate_capacitate", type: "text", required: true, descriere: "Unitate (buc, m, m²)" },
      { name: "capacitate_zi", type: "number", required: true, descriere: "Capacitate teoretică /zi" },
      { name: "capacitate_luna", type: "number", required: false, auto: true, descriere: "= capacitate_zi × zile (auto)" },
      { name: "incarcare_pct", type: "number", required: false, auto: true, descriere: "= produs / capacitate (auto)" },
    ],
    exampleRows: [
      ["Centru CNC vertical", "2025-07", "7", "28,0", "buc", "210,0", "1470,0", "1,9"],
      ["Mașină tăiere CNC", "2025-07", "20", "520,0", "m", "840,0", "16800,0", "3,1"],
    ],
    postProcess: (rows) =>
      rows.map((r) => {
        const capZi = r.capacitate_zi as number;
        const zile = r.zile_active as number;
        const cant = r.cant_produsa as number;
        const capLuna = capZi * zile;
        return {
          ...r,
          capacitate_luna: r1(capLuna),
          incarcare_pct: capLuna > 0 ? r1((cant / capLuna) * 100) : 0,
        };
      }),
  },
  {
    key: "productivitate_operatori",
    titlu: "4b. Productivitate operatori",
    descriere: "Ore lucrate, operații efectuate și valoarea generată per operator/centru, pe lună.",
    sursaSugerata: "Optima / pontaj — operații pe operator + valoare asociată",
    fileName: "template_04b_productivitate_operatori.csv",
    columns: [
      { name: "nume_operator", type: "text", required: true, descriere: "Nume operator" },
      { name: "centru_id", type: "text", required: true, descriere: "Cod centru de lucru" },
      { name: "luna_key", type: "luna", required: true, descriere: "Luna (AAAA-LL)" },
      { name: "ore_lucrate", type: "number", required: true, descriere: "Ore lucrate" },
      { name: "operatii_efectuate", type: "number", required: true, descriere: "Nr. operații" },
      { name: "valoare_operatii_eur", type: "number", required: true, descriere: "Valoare operații (€)" },
      { name: "output_eur_pe_ora", type: "number", required: false, auto: true, descriere: "= valoare / ore (auto)" },
      { name: "operatii_pe_ora", type: "number", required: false, auto: true, descriere: "= operații / ore (auto)" },
      { name: "rank_luna", type: "number", required: false, auto: true, descriere: "Clasament în lună (auto)" },
    ],
    exampleRows: [
      ["Operator A", "cnc_vertical", "2025-07", "152,0", "240", "30400,00", "200,00", "1,58", "1"],
      ["Operator B", "laminare", "2025-07", "148,0", "210", "26640,00", "180,00", "1,42", "2"],
    ],
    postProcess: (rows) => {
      const cu: RawRow[] = rows.map((r) => {
        const ore = r.ore_lucrate as number;
        const val = r.valoare_operatii_eur as number;
        const op = r.operatii_efectuate as number;
        return {
          ...r,
          output_eur_pe_ora: ore > 0 ? r2(val / ore) : 0,
          operatii_pe_ora: ore > 0 ? r2(op / ore) : 0,
        };
      });
      // rank per lună, descrescător după output €/oră
      const peLuna = new Map<string, RawRow[]>();
      for (const r of cu) {
        const k = r.luna_key as string;
        if (!peLuna.has(k)) peLuna.set(k, []);
        peLuna.get(k)!.push(r);
      }
      for (const grup of peLuna.values()) {
        grup
          .sort((a, b) => (b.output_eur_pe_ora as number) - (a.output_eur_pe_ora as number))
          .forEach((r, i) => {
            r.rank_luna = i + 1;
          });
      }
      return cu;
    },
  },
  {
    key: "export_split",
    titlu: "5a. Export vs. intern (split venit)",
    descriere: "Venit, marjă și nr. comenzi separate pe segmentul export vs. intern (RO).",
    sursaSugerata: "WinMentor — facturi vânzare, marcate export / intern (după țară client)",
    fileName: "template_05a_export_split.csv",
    columns: [
      { name: "segment", type: "enum", required: true, enumValues: ["export", "intern"], descriere: "export sau intern" },
      { name: "nr_comenzi", type: "number", required: true, descriere: "Număr comenzi" },
      { name: "venit_eur", type: "number", required: true, descriere: "Venit segment (€)" },
      { name: "pondere_venit_pct", type: "number", required: false, auto: true, descriere: "% din venit total (auto)" },
      { name: "pondere_comenzi_pct", type: "number", required: false, auto: true, descriere: "% din comenzi (auto)" },
      { name: "marja_eur", type: "number", required: true, descriere: "Marjă segment (€)" },
      { name: "marja_pct", type: "number", required: false, auto: true, descriere: "= marjă / venit (auto)" },
    ],
    exampleRows: [
      ["export", "61", "36463,03", "51,6", "49,2", "11232,14", "30,8"],
      ["intern", "63", "34177,44", "48,4", "50,8", "9381,24", "27,4"],
    ],
    postProcess: (rows) => {
      const totalVenit = rows.reduce((s, r) => s + (r.venit_eur as number), 0);
      const totalComenzi = rows.reduce((s, r) => s + (r.nr_comenzi as number), 0);
      return rows.map((r) => {
        const venit = r.venit_eur as number;
        const marja = r.marja_eur as number;
        return {
          ...r,
          pondere_venit_pct: totalVenit > 0 ? r1((venit / totalVenit) * 100) : 0,
          pondere_comenzi_pct: totalComenzi > 0 ? r1(((r.nr_comenzi as number) / totalComenzi) * 100) : 0,
          marja_pct: venit > 0 ? r1((marja / venit) * 100) : 0,
        };
      });
    },
  },
  {
    key: "export_trend",
    titlu: "5b. Evoluție lunară export %",
    descriere: "Ponderea exportului în venit, lună de lună (pentru graficul de trend).",
    sursaSugerata: "WinMentor — % venit export din total, pe fiecare lună",
    fileName: "template_05b_export_trend.csv",
    columns: [
      { name: "luna_key", type: "luna", required: true, descriere: "Luna (AAAA-LL)" },
      { name: "export_pct_venit", type: "number", required: true, descriere: "% export din venit" },
    ],
    exampleRows: [
      ["2025-07", "67,2"],
      ["2025-08", "50,8"],
    ],
  },
  {
    key: "comenzi",
    titlu: "6. Comenzi (fact granular) — alimentează filtrele + drill-down",
    descriere:
      "Comandă cu comandă: client, categorie, segment, mașină, venit, cost, termene. Sursa care face filtrele (perioadă/client/categorie/segment) și drill-down-ul să funcționeze pe TOATE rapoartele noi (Top clienți, Mix, Livrări).",
    sursaSugerata: "WinMentor — facturi vânzare linie cu linie + termene livrare (Optima)",
    fileName: "template_06_comenzi.csv",
    columns: [
      { name: "id", type: "text", required: true, descriere: "ID comandă / factură" },
      { name: "data", type: "date", required: true, descriere: "Data comenzii (AAAA-LL-ZZ)" },
      { name: "luna_key", type: "text", required: false, auto: true, descriere: "Luna (auto, din data)" },
      { name: "client", type: "text", required: true, descriere: "Nume client" },
      { name: "categorie", type: "enum", required: true, enumValues: ["geam_tren", "usi", "partitii", "vagoane", "constructii"], descriere: "Categorie produs" },
      { name: "segment", type: "enum", required: true, enumValues: ["export", "intern"], descriere: "export sau intern" },
      { name: "masina", type: "text", required: true, descriere: "Mașină/centru principal" },
      { name: "cantitate_m2", type: "number", required: true, descriere: "Cantitate (m²)" },
      { name: "venit_eur", type: "number", required: true, descriere: "Venit comandă (€)" },
      { name: "cost_eur", type: "number", required: true, descriere: "Cost comandă (€)" },
      { name: "termen_promis", type: "date", required: true, descriere: "Termen livrare promis (AAAA-LL-ZZ)" },
      { name: "termen_realizat", type: "date", required: true, descriere: "Termen livrare realizat (AAAA-LL-ZZ)" },
      { name: "marja_eur", type: "number", required: false, auto: true, descriere: "= venit − cost (auto)" },
      { name: "marja_pct", type: "number", required: false, auto: true, descriere: "= marjă / venit (auto)" },
      { name: "intarziere_zile", type: "number", required: false, auto: true, descriere: "= realizat − promis (auto)" },
    ],
    exampleRows: [
      ["CMD-0001", "2025-07-21", "2025-07", "Vago Rail Austria", "geam_tren", "export", "Masina taiere CNC", "25,2", "15646,96", "9625,21", "2025-08-02", "2025-08-02", "6021,75", "38,5", "0"],
      ["CMD-0002", "2025-07-14", "2025-07", "Imobiliare Beta SA", "constructii", "intern", "Cuptor securizare", "12,4", "4980,00", "3735,00", "2025-07-30", "2025-08-01", "1245,00", "25,0", "2"],
    ],
    postProcess: (rows) =>
      rows.map((r) => {
        const venit = r.venit_eur as number;
        const cost = r.cost_eur as number;
        const marja = venit - cost;
        const data = r.data as string;
        return {
          ...r,
          luna_key: data.slice(0, 7),
          marja_eur: r2(marja),
          marja_pct: venit > 0 ? r1((marja / venit) * 100) : 0,
          intarziere_zile: daysBetween(r.termen_promis as string, r.termen_realizat as string),
        };
      }),
  },
  {
    key: "pierderi",
    titlu: "7. Pierderi / waste pe operații",
    descriere: "Material intrat vs. rebut pe fiecare operație din traseu (tăiere, șlefuire, laminare, călire…), pe lună.",
    sursaSugerata: "Optima — traseu tehnologic, consum vs. rebut per fază",
    fileName: "template_07_pierderi.csv",
    columns: [
      { name: "luna_key", type: "luna", required: true, descriere: "Luna (AAAA-LL)" },
      { name: "operatie", type: "text", required: true, descriere: "Denumire operație" },
      { name: "intrare_m2", type: "number", required: true, descriere: "Material intrat (m²)" },
      { name: "pierdere_m2", type: "number", required: true, descriere: "Rebut / pierdere (m²)" },
      { name: "pierdere_pct", type: "number", required: false, auto: true, descriere: "= pierdere / intrare (auto)" },
      { name: "cost_pierdere_eur", type: "number", required: false, auto: true, descriere: "= pierdere × 120 €/m² (auto)" },
    ],
    exampleRows: [
      ["2025-07", "Tăiere", "1289,8", "102,9", "8", "12348"],
      ["2025-07", "Șlefuire muchii", "1226,5", "27,6", "2,2", "3312"],
    ],
    postProcess: (rows) =>
      rows.map((r) => {
        const intrare = r.intrare_m2 as number;
        const pierdere = r.pierdere_m2 as number;
        return {
          ...r,
          pierdere_pct: intrare > 0 ? r1((pierdere / intrare) * 100) : 0,
          cost_pierdere_eur: r2(pierdere * 120),
        };
      }),
  },
];

export function datasetByKey(key: DatasetKey): DatasetDef {
  return DATASETS.find((d) => d.key === key)!;
}
